import React, { useMemo, useRef } from "react";

import { Autocomplete, Chip, SxProps, TextField, Theme } from "@mui/material";
import { calcFieldsToDisplay } from "constants/reports";
import { useCharacteristics, useSimpleMeasures } from "hooks";
import { difference, groupBy } from "lodash";
import { useCalculatedSelector } from "store";
import { ReportsQueries } from "store/types";

import { Group } from "./Group";

type Option = { type: string; option: string | number };

type GroupKeys = keyof Pick<ReportsQueries, "measures" | "calculatedFields" | "characteristics">;

const groupsKeyTitle: Record<GroupKeys, string> = {
  measures: "Measures",
  characteristics: "Characteristics",
  calculatedFields: "Calculated Measures"
};

interface AutocompleteMeasuresProps {
  label: string;
  queries: Partial<Pick<ReportsQueries, "measures" | "calculatedFields" | "characteristics">>;
  setQueryValue: (field: keyof ReportsQueries, value: (string | number)[]) => void;
  setValues?: (value: (string | number)[]) => void;
  onClear?: () => void;
  multiple?: boolean;
  showGroups?: GroupKeys[];
  disableClearable?: boolean;
  excludeStaticCalcFields?: boolean;
  sx?: SxProps<Theme>;
}

export const AutocompleteMeasures = ({
  label,
  queries,
  setQueryValue,
  setValues,
  onClear,
  multiple = true,
  disableClearable,
  showGroups = ["measures", "calculatedFields", "characteristics"],
  excludeStaticCalcFields,
  sx
}: AutocompleteMeasuresProps) => {
  const characteristicsAll = useCharacteristics();
  const calculatedFieldsAll = useCalculatedSelector();
  const simpleMeasures = useSimpleMeasures();
  const uniqueCalFieldsToShow = difference(calculatedFieldsAll, calcFieldsToDisplay);

  const [options, selectedOptions] = useMemo(() => {
    let options: Option[] = [];
    if (showGroups.includes("measures")) {
      options = simpleMeasures.map((m) => ({ type: groupsKeyTitle.measures, option: m }));
    }
    if (showGroups.includes("characteristics")) {
      options = [
        ...options,
        ...characteristicsAll.map((c) => ({
          type: groupsKeyTitle.characteristics,
          option: typeof c === "string" ? c : c.value
        }))
      ];
    }
    if (showGroups.includes("calculatedFields")) {
      //if  excludeStaticCalcFields is true, then we need to remove from arr list of specific calculated fields (calcFieldsToDisplay),
      //because we show them in the table as static buttons in table toolbar
      excludeStaticCalcFields
        ? (options = [
            ...options,
            ...uniqueCalFieldsToShow.map((cal) => ({ type: groupsKeyTitle.calculatedFields, option: cal }))
          ])
        : (options = [
            ...options,
            ...calculatedFieldsAll.map((cal) => ({ type: groupsKeyTitle.calculatedFields, option: cal }))
          ]);
    }

    const selectedOptions: Option[] = [
      ...(queries.measures || []),
      ...(queries.calculatedFields || []),
      ...(queries.characteristics || [])
    ]
      .map((value) => options.find((option) => option.option === value))
      .filter(Boolean) as Option[];

    return [options, selectedOptions];
  }, [
    simpleMeasures,
    characteristicsAll,
    calculatedFieldsAll,
    uniqueCalFieldsToShow,
    queries,
    excludeStaticCalcFields
  ]);

  const handleSetColumns = (value: ReadonlyArray<Option>, reason?: string) => {
    if (reason === "clear" && onClear) {
      onClear();
      return;
    }

    const groups = groupBy(value, (v) => v.type);
    setQueryValue(
      "measures",
      (groups["Measures"] || []).map((val) => val.option)
    );
    setQueryValue(
      "characteristics",
      (groups["Characteristics"] || []).map((val) => val.option)
    );
    setQueryValue(
      "calculatedFields",
      (groups["Calculated Measures"] || []).map((val) => val.option)
    );
    setValues?.(value.map((val) => val.option));
  };

  const popperRef = useRef<any>();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Autocomplete<Option, boolean, boolean>
      sx={sx || { minWidth: 200 }}
      disableClearable={disableClearable}
      disableCloseOnSelect={multiple}
      ListboxProps={{ style: { padding: 0, minHeight: "18vh", maxHeight: "50vh" } }}
      value={multiple ? selectedOptions : selectedOptions[0]}
      limitTags={3}
      options={options}
      groupBy={(option) => option.type}
      multiple={multiple}
      onChange={(_: any, value, reason) => {
        handleSetColumns(!value ? [] : Array.isArray(value) ? value : [value], reason);
      }}
      getOptionLabel={(option) => option.option.toString()}
      renderInput={(params) => <TextField inputRef={inputRef} {...params} label={label} />}
      slotProps={{
        popper: { popperRef: popperRef },
        listbox: { sx: { "& .MuiAutocomplete-option:hover": { backgroundColor: "#f5f5f5" } } }
      }}
      renderTags={(tagValue, getTagProps) => {
        return (
          <div style={{ maxHeight: "110px", overflowY: "auto", overflowX: "hidden" }}>
            {tagValue.map((option, index) => (
              <Chip {...getTagProps({ index })} key={index} label={option.option?.toString() || ""} />
            ))}
          </div>
        );
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <Group params={params} popperRef={popperRef} searchValue={inputRef.current?.value || ""} />
        </li>
      )}
    />
  );
};
